import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { DataTable } from 'primereact/datatable'
import { FileUpload, FileUploadHandlerParam } from 'primereact/fileupload'
import { Toast } from 'primereact/toast'
import React, { useRef, useState } from 'react'
import { claims } from '../../../claims'
import {
  API_LEARNING_JOURNAL_FILE,
  API_LEARNING_JOURNAL_FILE_DOWNLOAD,
  API_LEARNING_JOURNAL_UPLOAD,
  MAX_JOURNAL_MULTIUPLOAD,
  MAX_JOURNAL_SIZE,
} from '../../../constants'
import { signedFileUrlsWithIds } from '../../../model/dossier.model'
import { RequestMethod } from '../../../model/enums/enums'
import {
  learningJournal,
  learningJournalFile,
} from '../../../model/learnJournal.model'
import { SignedUrlResponse } from '../../../model/signedUrlResponse.model'
import { useAppSelector } from '../../../redux/hooks'
import { handleRequest } from '../../../services/api/base/handleRequest'
import { findPersonById } from '../../../util/arraySearch'
import { hasRequiredClaim } from '../../../util/validation/hasClaim'
import { dateBody } from '../../util/data-table/cell-body/BirthdayBody'
import { AvatarCell } from '../../util/data-table/cell-body/FullNameApprenticeBody'
import { FileTypeIcon } from '../../util/icon/FileTypeIcon'
import { sortByPersonNameFromInnerPersonId } from '../../util/sort-functions/TableSortFunctions'
import { supportsFileType } from '../dossier/Helpers'

export const JournalFileDatatable = (props: {
  currentJournal: learningJournal
  editMode: boolean
  deleteButtonDisabled: boolean
  personId: number
  toast: React.RefObject<Toast>
  setJournal: (lj: learningJournal) => void
}): JSX.Element => {
  const fileUploadRef = useRef<FileUpload>(null)

  const apprentices = useAppSelector((state) => state.apprentices)
  const employees = useAppSelector((state) => state.employees)
  const { personId } = useAppSelector((state) => state.user)
  const userClaims = useAppSelector((state) => state.claims)
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
  const [fileToDelete, setFileToDelete] = useState<learningJournalFile>()

  const footer = () => {
    if (props.editMode) {
      return (
        <FileUpload
          mode="basic"
          chooseLabel={t<string>('core.upload_file')}
          multiple={true}
          auto={true}
          customUpload={true}
          uploadHandler={onFileUpload}
          ref={fileUploadRef}
        ></FileUpload>
      )
    }
  }

  const onFileUpload = async (e: FileUploadHandlerParam) => {
    const curJournalFileLength =
      props.currentJournal.learning_journal_files?.length || 0
    // too many files
    if (curJournalFileLength + e.files.length > MAX_JOURNAL_MULTIUPLOAD) {
      props.toast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('dossier.files_exceed.title'),
        detail: t<string>('dossier.files_exceed.message', {
          num: MAX_JOURNAL_MULTIUPLOAD,
        }),
        life: 4000,
      })
      fileUploadRef.current?.clear()
      return
    }

    if (e.files.filter((f) => f.size === 0).length > 0) {
      props.toast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('dossier.empty_file.title'),
        detail: t<string>('dossier.empty_file.message'),
      })
      fileUploadRef.current?.clear()
      return
    }

    for (let f of e.files) {
      if (
        props.currentJournal?.learning_journal_files?.find(
          (file) => file.file_name === f.name
        )
      ) {
        props.toast.current?.show({
          closable: true,
          severity: 'error',
          summary: t<string>('journal.toasts.duplicate_file.title'),
          detail: t<string>('journal.toasts.duplicate_file.message', {
            name: f.name,
          }),
          life: 4000,
        })
        fileUploadRef.current?.clear()
        return
      }
    }

    let curJournalSize = 0
    if (props.currentJournal.learning_journal_files) {
      curJournalSize += props.currentJournal.learning_journal_files.reduce(
        (prev, cur) => prev + cur.size,
        0
      )
    }
    curJournalSize += e.files.reduce((prev, cur) => prev + cur.size, 0)
    if (curJournalSize > MAX_JOURNAL_SIZE) {
      const maxSize = `${MAX_JOURNAL_SIZE / 1e6}Mb`
      props.toast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('journal.file_size.title'),
        detail: t<string>('journal.file_size.message', { max_size: maxSize }),
        life: 4000,
      })
      fileUploadRef.current?.clear()
      return
    }

    for (const f of e.files) {
      const spl = f.name.split('.')
      const type = spl[spl.length - 1]
      if (spl.length < 2 || !supportsFileType(type)) {
        props.toast.current?.show({
          closable: true,
          severity: 'error',
          summary: t<string>('dossier.type_no_support.title'),
          detail: t<string>('dossier.type_no_support.message', { type: type }),
          life: 4000,
        })
        fileUploadRef.current?.clear()
        return
      }

      if (
        !spl[0].match(
          /^[\u00C0-\u017Fa-zA-Z0-9](?:[\u00C0-\u017Fa-zA-Z0-9()._-]*[\u00C0-\u017Fa-zA-Z0-9)])?$/
        )
      ) {
        props.toast.current?.show({
          closable: true,
          severity: 'error',
          summary: t<string>('dossier.type_no_support.title'),
          detail: t<string>('dossier.invalid_name'),
          life: 9000,
        })
        fileUploadRef.current?.clear()
        return
      }
    }
    const files: learningJournalFile[] = []
    for (const f of e.files) {
      const body: learningJournalFile = {
        file_name: f.name,
        size: f.size,
        id_creator: props.personId,
        created_on: new Date(),
      }
      files.push(body)
    }
    if (!props.currentJournal.learning_journal_id) {
      fileUploadRef.current?.clear()
      return
    }
    const result = await handleRequest<signedFileUrlsWithIds>(
      API_LEARNING_JOURNAL_UPLOAD(props.currentJournal?.learning_journal_id),
      RequestMethod.POST,
      files
    )

    if (result.error) {
      props.toast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('core.error.add.title'),
        detail: t<string>('core.error.add.message'),
        life: 4000,
      })
      fileUploadRef.current?.clear()
      return
    }

    const signedUrls = result?.data?.url_arr
    if (!signedUrls) {
      console.error('signed urls are not provided')
      fileUploadRef.current?.clear()
      return
    }

    if (signedUrls.length !== files.length) {
      console.error('URL and File missmatch')
      fileUploadRef.current?.clear()
      return
    }
    let i = 0
    fileUploadRef.current?.clear()
    let insertId = result.data?.firstInsertId
    if (!insertId) {
      console.error(
        '[Journal Entry] No insertID was given. You need a refresh to see the data'
      )
    }
    for (let url of signedUrls) {
      const uploadResult = await handleRequest<any>(
        url,
        RequestMethod.UPLOAD,
        e.files[i],
        undefined,
        true
      )
      // this should not happen
      if (uploadResult.error) {
        console.error(uploadResult)
        //TODO handle error
      }
      if (insertId) {
        files[i].journal_file_id = insertId
        insertId += 1
      }
      i += 1
    }

    props.toast.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('dossier.upload.title'),
      detail: t<string>('dossier.upload.message'),
      life: 4000,
    })
    const currentJournalCopy = { ...props.currentJournal }
    if (props.currentJournal.learning_journal_files) {
      currentJournalCopy.learning_journal_files = [
        ...props.currentJournal.learning_journal_files,
        ...files,
      ]
    } else {
      currentJournalCopy.learning_journal_files = [...files]
    }
    props.setJournal(currentJournalCopy)
    fileUploadRef.current?.clear()
  }

  const getAvatarWithName = (person_id: number) => {
    let p = findPersonById(person_id, apprentices)
    if (!p) {
      p = findPersonById(person_id, employees)
    }
    return AvatarCell(p)
  }

  const downloadDocument = async (learningJournalFileId: number) => {
    const downloadLinkResult = await handleRequest<SignedUrlResponse>(
      API_LEARNING_JOURNAL_FILE_DOWNLOAD(learningJournalFileId),
      RequestMethod.GET
    )
    const signedUrl = downloadLinkResult?.data?.url
    console.log(signedUrl)
    if (signedUrl) {
      const newWindow: Window | null = window.open(signedUrl, '_blank')
      if (newWindow) {
        newWindow.location = signedUrl
        newWindow.opener = null
        return
      }
    } else {
      props.toast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('journal.toasts.resource_missing.title'),
        detail: t<string>('journal.toasts.resource_missing.message'),
        life: 8000,
      })
    }
  }

  const deleteDocument = async () => {
    const learningJournalFileId = fileToDelete?.journal_file_id
    if (!learningJournalFileId) {
      props.toast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('core.action.delete.error.title'),
        detail: t<string>('core.action.delete.error.message'),
        life: 12000,
      })
      console.warn('[Learning Journal] Journal File Id is not a number.')
      return
    }
    const result = await handleRequest<void>(
      API_LEARNING_JOURNAL_FILE(learningJournalFileId),
      RequestMethod.DELETE
    )

    if (result.error) {
      props.toast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('core.action.delete.error.title'),
        detail: t<string>('core.action.delete.error.message'),
        life: 12000,
      })
      console.warn(
        '[Learning Journal] Error occurred on deleting the file.',
        result.error
      )
      return
    }

    let filteredState = props.currentJournal.learning_journal_files?.filter(
      (e) => e.journal_file_id !== learningJournalFileId
    )
    props.toast.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('core.action.delete.confirmed.title'),
      detail: t<string>('core.action.delete.confirmed.message'),
      life: 8000,
    })
    if (!filteredState) {
      filteredState = []
    }
    const currentJournalCopy: learningJournal = { ...props.currentJournal }
    currentJournalCopy.learning_journal_files = filteredState
    props.setJournal(currentJournalCopy)
    resetDeleteDialog()
  }

  const resetDeleteDialog = () => {
    setFileToDelete(undefined)
    setOpenDeleteDialog(false)
  }

  const rowEditorTemplate = (currentFile: learningJournalFile) => {
    const isNotOwnFile = currentFile.id_creator !== personId
    const disabled =
      isNotOwnFile &&
      !hasRequiredClaim(userClaims, claims.learningJournals.review.update)
    return (
      <>
        <Button
          className="p-button-text icon-orange"
          icon="fa-solid fa-file-arrow-down fa-lg"
          tooltip={t<string>('core.download')}
          type={'button'}
          tooltipOptions={{ position: 'left', showDelay: 500 }}
          onClick={(_) => downloadDocument(currentFile.journal_file_id)}
        />
        <Button
          icon="fas fa-trash"
          className="p-button-danger p-button-text ml-auto"
          tooltip={t<string>('core.delete')}
          type={'button'}
          tooltipOptions={{ position: 'left', showDelay: 500 }}
          onClick={(_) => {
            setFileToDelete(currentFile)
            setOpenDeleteDialog(true)
          }}
          disabled={props.deleteButtonDisabled || disabled}
        />
      </>
    )
  }
  const getFileTypeIcon = (filename: string) => {
    const spl = filename.split('.')
    const ending = spl[spl.length - 1]
    return FileTypeIcon(ending)
  }

  return (
    <>
      <div className="flex align-items-center pt-2">
        <DataTable
          value={props.currentJournal.learning_journal_files}
          responsiveLayout="scroll"
          rowHover={true}
          stripedRows={true}
          resizableColumns
          filterDisplay="row"
          footer={footer()}
          removableSort
          className="footer justify-content-end w-full"
        >
          <Column
            body={(e) => getFileTypeIcon(e.file_name)}
            dataType={'text'}
          />
          <Column
            header={t<string>('core.file_name')}
            field="file_name"
            sortable
          />
          <Column
            header={t<string>('core.file_creator')}
            field={'id_creator'}
            sortable
            dataType={'numeric'}
            showFilterMenu={false}
            body={(e) => getAvatarWithName(e.id_creator)}
            sortFunction={(e) =>
              sortByPersonNameFromInnerPersonId(
                e,
                apprentices.concat(employees)
              )
            }
          />
          <Column
            header={t<string>('dossier.size')}
            field={'size'}
            dataType={'numeric'}
            sortable
            body={(e) => `${(e.size / 1e3).toFixed(2)}kB`}
            editor={(e) => `${(e.value / 1e3).toFixed(2)}kB`}
            resizeable={false}
          />

          <Column
            header={t<string>('dossier.created_on')}
            field={'created_on'}
            sortable
            dataType={'date'}
            bodyClassName={'small-text secondary-text'}
            editor={(e) => dateBody(e.value)}
            body={(e) => dateBody(e.created_on)}
          />
          <Column
            body={(e) => rowEditorTemplate(e)}
            style={{
              textAlign: 'center',
              width: '120px',
              minWidth: '120px',
              maxWidth: '120px',
            }}
          />
        </DataTable>
      </div>

      <ConfirmDialog
        visible={openDeleteDialog}
        header={t<string>('dossier.delete_file.title')}
        message={t<string>('dossier.delete_file.message', {
          name: fileToDelete?.file_name,
        })}
        onHide={resetDeleteDialog}
        reject={resetDeleteDialog}
        accept={deleteDocument}
      />
    </>
  )
}